import React, { useMemo } from 'react';
import {
  Image,
  Pressable,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { fpColors } from '../../../../styles/theme';
import type { InventoryItemResponse } from '../../../../api/types';
import type { PickerSection } from './buildPickerSections';

/**
 * SceneSection — FE-10.1.6 picker scene card.
 *
 * Layout (collapsed):
 *   ┌──────────────────────────────────────────────┐
 *   │ ┌────────┐  Trailer scan          ▾          │
 *   │ │ hero   │  Apr 28 · 14 items                │
 *   │ │ photo  │  [ Select all 14 ]                │
 *   │ └────────┘                                   │
 *   └──────────────────────────────────────────────┘
 *
 * Layout (expanded):
 *   ┌──────────────────────────────────────────────┐
 *   │ ▾ Trailer scan · Apr 28                      │
 *   │   14 items · 12 selected                     │
 *   │   [ Deselect all 14 ]                        │
 *   │   ──────────────────                         │
 *   │   ┌──┐☑ ┌──┐☑ ┌──┐☐ ┌──┐☑                    │
 *   │   ...                                         │
 *   └──────────────────────────────────────────────┘
 *
 * Selection state is owned by the parent; this component is a
 * controlled component. Both `selectedIds` and `onToggle` /
 * `onToggleAll` flow in via props so the parent's footer counter
 * stays in sync without a custom event bus.
 *
 * Generic shape: works for all three picker modes (scenes / category /
 * recent), but the hero photo only renders when `section.heroPhotoUrl`
 * is set (Scenes mode).
 */

interface SceneSectionProps {
  section: PickerSection;
  /** Globally-selected item ids — read to compute `selectedInSection`. */
  selectedIds: Set<number>;
  /** Whether this section is currently expanded. */
  expanded: boolean;
  /** Toggle a single item's selection. */
  onToggle: (itemId: number) => void;
  /** Toggle every item in the section. When all are selected this
   *  acts as Deselect-all; otherwise Select-all. */
  onToggleAll: (itemIds: number[], targetState: boolean) => void;
  /** Toggle the section's expanded state. */
  onExpandToggle: () => void;
}

const THUMB_SIZE = 64;

export const SceneSection: React.FC<SceneSectionProps> = ({
  section,
  selectedIds,
  expanded,
  onToggle,
  onToggleAll,
  onExpandToggle,
}) => {
  const eligibleIds = useMemo(
    () => section.items.map((it) => it.id).filter((id): id is number => id != null),
    [section.items],
  );

  const selectedInSection = useMemo(() => {
    let count = 0;
    for (const id of eligibleIds) if (selectedIds.has(id)) count++;
    return count;
  }, [eligibleIds, selectedIds]);

  const allSelected = selectedInSection === eligibleIds.length && eligibleIds.length > 0;
  const totalCount = eligibleIds.length;

  const handleSelectAllPress = () => {
    onToggleAll(eligibleIds, !allSelected);
  };

  const sectionTone = allSelected
    ? styles.cardSelected
    : selectedInSection > 0
    ? styles.cardPartial
    : null;

  return (
    <View style={[styles.card, sectionTone]} testID={`picker-section-${section.key}`}>
      {/* Header row */}
      <Pressable
        onPress={onExpandToggle}
        accessibilityRole="button"
        accessibilityState={{ expanded }}
        accessibilityLabel={`Expand ${section.title}`}
        style={styles.headerRow}
        testID={`picker-section-header-${section.key}`}
      >
        {section.heroPhotoUrl ? (
          <Image
            source={{ uri: section.heroPhotoUrl }}
            style={styles.hero}
            accessibilityIgnoresInvertColors
          />
        ) : (
          <View style={[styles.hero, styles.heroPlaceholder]}>
            <MaterialIcons
              name={section.mode === 'category' ? 'label' : 'photo-library'}
              size={22}
              color={fpColors.outline}
            />
          </View>
        )}
        <View style={styles.headerText}>
          <Text style={styles.title} numberOfLines={1}>
            {section.title}
          </Text>
          {section.subtitle ? (
            <Text style={styles.subtitle} numberOfLines={1}>
              {section.subtitle}
              {selectedInSection > 0
                ? ` · ${selectedInSection} selected`
                : ''}
            </Text>
          ) : null}
          <Pressable
            onPress={(e) => {
              if (typeof (e as any)?.stopPropagation === 'function') {
                (e as any).stopPropagation();
              }
              handleSelectAllPress();
            }}
            accessibilityRole="button"
            accessibilityLabel={
              allSelected
                ? `Deselect all ${totalCount}`
                : `Select all ${totalCount}`
            }
            style={({ pressed }) => [
              styles.selectAllBtn,
              allSelected && styles.selectAllBtnActive,
              pressed && { opacity: 0.85 },
            ]}
            testID={`picker-select-all-${section.key}`}
          >
            <MaterialIcons
              name={allSelected ? 'check-box' : 'check-box-outline-blank'}
              size={16}
              color={allSelected ? '#FFFFFF' : fpColors.primary}
            />
            <Text
              style={[
                styles.selectAllText,
                allSelected && styles.selectAllTextActive,
              ]}
            >
              {allSelected
                ? `Deselect all ${totalCount}`
                : `Select all ${totalCount}`}
            </Text>
          </Pressable>
        </View>
        <MaterialIcons
          name={expanded ? 'expand-less' : 'expand-more'}
          size={22}
          color={fpColors.outline}
        />
      </Pressable>

      {/* Drill-in grid */}
      {expanded ? (
        <View style={styles.grid}>
          {section.items.map((it) => {
            const id = it.id as number;
            const checked = selectedIds.has(id);
            const photo =
              (it as any).primary_photo_url ?? (it as any).source_image_url;
            return (
              <Pressable
                key={id}
                onPress={() => onToggle(id)}
                accessibilityRole="checkbox"
                accessibilityState={{ checked }}
                accessibilityLabel={`Select ${it.item_name ?? 'item'}`}
                style={[styles.thumbWrap, checked && styles.thumbWrapChecked]}
                testID={`picker-thumb-${id}`}
              >
                {photo ? (
                  <Image
                    source={{ uri: photo }}
                    style={styles.thumb}
                    accessibilityIgnoresInvertColors
                  />
                ) : (
                  <View style={[styles.thumb, styles.thumbPlaceholder]}>
                    <MaterialIcons
                      name="image"
                      size={18}
                      color={fpColors.outline}
                    />
                  </View>
                )}
                <Text style={styles.thumbName} numberOfLines={1}>
                  {it.item_name ?? 'Unnamed'}
                </Text>
                <View
                  style={[
                    styles.checkOverlay,
                    checked && styles.checkOverlayChecked,
                  ]}
                >
                  <MaterialIcons
                    name={checked ? 'check' : 'add'}
                    size={14}
                    color="#FFFFFF"
                  />
                </View>
              </Pressable>
            );
          })}
        </View>
      ) : null}
    </View>
  );
};

/* ──────────────────────────────────────────────────────────────────────
 * Styles
 * ────────────────────────────────────────────────────────────────────── */

const styles = StyleSheet.create({
  card: {
    marginHorizontal: 14,
    marginVertical: 6,
    backgroundColor: fpColors.surfaceContainerLowest,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
  },
  cardPartial: {
    borderColor: fpColors.primary,
  },
  cardSelected: {
    borderColor: fpColors.primary,
    backgroundColor: fpColors.primaryContainer,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    padding: 12,
  },
  hero: {
    width: 64,
    height: 64,
    borderRadius: 10,
    backgroundColor: fpColors.surfaceContainerLow,
  },
  heroPlaceholder: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerText: {
    flex: 1,
    minWidth: 0,
  },
  title: {
    fontFamily: 'Inter_700Bold',
    fontSize: 15,
    color: fpColors.onSurface,
  },
  subtitle: {
    fontFamily: 'Inter_400Regular',
    fontSize: 12,
    color: fpColors.outline,
    marginTop: 2,
  },
  selectAllBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    alignSelf: 'flex-start',
    marginTop: 8,
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: fpColors.primary,
    backgroundColor: 'transparent',
  },
  selectAllBtnActive: {
    backgroundColor: fpColors.primary,
  },
  selectAllText: {
    fontFamily: 'Inter_600SemiBold',
    fontSize: 12,
    color: fpColors.primary,
  },
  selectAllTextActive: {
    color: '#FFFFFF',
  },
  grid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    paddingHorizontal: 12,
    paddingBottom: 14,
  },
  thumbWrap: {
    width: THUMB_SIZE + 16,
    alignItems: 'center',
    paddingHorizontal: 4,
    paddingVertical: 6,
    borderRadius: 10,
    position: 'relative',
  },
  thumbWrapChecked: {
    backgroundColor: 'rgba(125,52,183,0.10)',
  },
  thumb: {
    width: THUMB_SIZE,
    height: THUMB_SIZE,
    borderRadius: 8,
    backgroundColor: fpColors.surfaceContainerLow,
  },
  thumbPlaceholder: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  thumbName: {
    fontFamily: 'Inter_400Regular',
    fontSize: 11,
    color: fpColors.onSurface,
    marginTop: 4,
    textAlign: 'center',
  },
  checkOverlay: {
    position: 'absolute',
    top: 4,
    right: 4,
    width: 22,
    height: 22,
    borderRadius: 11,
    backgroundColor: 'rgba(28, 23, 33, 0.55)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkOverlayChecked: {
    backgroundColor: fpColors.primary,
  },
});

export default SceneSection;
